import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { TESTIMONIALS } from "@/data/site";
import { Reveal, SectionHead, Accent } from "@/components/Reveal";

const Stars = ({ n }) => (
  <div className="flex gap-1" aria-label={`${n} star rating`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={15} className="fill-[#F2C230] text-[#F2C230]" />
    ))}
  </div>
);

const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [index, setIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => clearInterval(t);
  }, [emblaApi]);

  return (
    <section className="py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          align="center"
          eyebrow="Traveller Stories"
          title={<>Postcards from people who <Accent>went</Accent></>}
        />
      </div>
      <Reveal delay={0.15}>
        <div data-testid="testimonials-slider" className="mt-14 overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef}>
          <div className="flex">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name} className="flex-[0_0_88%] sm:flex-[0_0_60%] lg:flex-[0_0_42%] px-3">
                <figure
                  className={`glass rounded-3xl p-8 h-full transition-opacity duration-500 ${
                    index === i ? "opacity-100" : "opacity-45"
                  }`}
                >
                  <Quote size={26} className="text-[#3FB6D3]" />
                  <blockquote className="text-base text-[#F5F5F0]/85 leading-relaxed mt-5 min-h-[120px]">
                    {t.quote}
                  </blockquote>
                  <figcaption className="flex items-center gap-4 mt-7">
                    <img src={t.avatar} alt={t.name} loading="lazy" className="w-12 h-12 rounded-full object-cover border-2 border-[#F2C230]/50" />
                    <div>
                      <p className="font-sora text-sm font-semibold">{t.name}</p>
                      <p className="text-xs text-[#3FB6D3] mt-0.5">{t.trip}</p>
                    </div>
                    <div className="ml-auto">
                      <Stars n={t.rating} />
                    </div>
                  </figcaption>
                </figure>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center gap-4 mt-9">
          <button
            data-testid="testimonial-prev"
            onClick={() => emblaApi && emblaApi.scrollPrev()}
            aria-label="Previous testimonial"
            className="w-11 h-11 rounded-full glass flex items-center justify-center text-[#F5F5F0]/80 hover:text-[#F2C230] hover:border-[#F2C230]/40 transition-colors duration-300"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
                  index === i ? "w-7 bg-[#F2C230]" : "w-1.5 bg-white/25"
                }`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next"
            onClick={() => emblaApi && emblaApi.scrollNext()}
            aria-label="Next testimonial"
            className="w-11 h-11 rounded-full glass flex items-center justify-center text-[#F5F5F0]/80 hover:text-[#F2C230] hover:border-[#F2C230]/40 transition-colors duration-300"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </Reveal>
    </section>
  );
};

export default Testimonials;
